import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RestResponse } from 'app/modules/common/entities/rest.response-generic.model';
import { DateTimeUtilsService } from 'app/modules/common/services/date-time-utils.service';
import { PageLoaderService } from 'app/modules/common/services/page-loader.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import { Actuation } from 'app/modules/common/types/actuation.type';
import { Baja } from 'app/modules/common/types/baja.type';
import { DropDownOption } from 'app/modules/common/types/dropdown-option.type';
import { DateValidators } from 'app/modules/common/validators/date.validators';
import { DependantValidators } from 'app/modules/common/validators/dependant.validators';
import { LeaveService } from 'app/modules/leave/services/leave.service';
import moment from 'moment';
import { ActuationService } from '../../services/actuation.service';

export type EditSolicitudDialogData = {
    actuationId: number;
    leaveId: number;
};

const REPLY_TYPES: DropDownOption<string>[] = [
    { value: null, viewValue: 'N/A' },
    { value: 'APROBADA', viewValue: 'Aprobada' },
    { value: 'PENDIENTE', viewValue: 'Pdte. Contestar' },
    { value: 'RECHAZADA', viewValue: 'Rechazada' },
];

const JUSTIFICATIONS_LIST: DropDownOption<string>[] = [
    { value: null, viewValue: 'N/A' },
    { value: 'NO MOTIVADO', viewValue: 'No motivado' },
    { value: 'MOTIVADO', viewValue: 'Motivado' },
];

@Component({
    selector: 'app-edit-solicitud-dialog',
    templateUrl: './edit-solicitud-dialog.component.html',
    styleUrls: ['./edit-solicitud-dialog.component.scss'],
    standalone: false
})
export class EditSolicitudDialogComponent implements OnInit {
    form: UntypedFormGroup;
    title = '';
    replyTypes = REPLY_TYPES;
    justificationsList = JUSTIFICATIONS_LIST;

    constructor(
        public dialogRef: MatDialogRef<EditSolicitudDialogComponent>,
        private fb: UntypedFormBuilder,
        private actuationService: ActuationService,
        private pageLoader: PageLoaderService,
        private sharedService: SharedService,
        private leaveService: LeaveService,
        private dateUtils: DateTimeUtilsService,
        @Inject(MAT_DIALOG_DATA) private data: EditSolicitudDialogData
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.getData();
    }

    //#region INIT

    private initForm() {
        this.form = this.fb.group({
            fecha: null,
            respuestaSolicitud: null,
            frespuestaSolicitud: null,
            fprogramadaSolicitud: null,
            frealizacionSolicitud: null,
            justificacionSolicitud: null,
            comentarios: null,
        });
    }

    //#endregion

    //#region EVENTS

    /**
     * Save button click
     */
    async save() {
        const formValue = this.convertFormToSolicitud();

        // update actuation
        this.pageLoader.ShowPageLoader();
        const updateSolicitud = await this.updateSolicitud(
            this.data.actuationId,
            formValue
        );
        this.pageLoader.HidePageLoader();

        if (updateSolicitud) this.dialogRef.close(true);
        else
            this.sharedService.ShowSnackBar(
                'Lo sentimos, la solicitud de actualización ha fallado'
            );
    }

    /**
     * Close button click
     */
    close() {
        this.dialogRef.close(false);
    }

    /**
     * Repusta type change
     */
    replyTypeChange() {
        if (!this.form.value.respuestaSolicitud) {
            this.form.patchValue({
                frespuestaSolicitud: null,
                justificacionSolicitud: null,
            });
        }
        this.form.updateValueAndValidity();
    }

    //#endregion

    //#region MECHANICS

    /**
     * Get all data for component
     */
    private async getData() {
        setTimeout(() => this.pageLoader.ShowPageLoader(), 0);
        const leave = await this.getLeaveData(this.data.leaveId);
        const actuation = await this.getActuationData(this.data.actuationId);
        setTimeout(() => this.pageLoader.HidePageLoader(), 0);

        if (!actuation || !leave) {
            this.sharedService.ShowSnackBar(
                'Lo siento, ha fallado la obtención de los datos de la licencia o de la acción'
            );
            this.close();
            return;
        }

        this.fillData(actuation);
        this.setValidationsFromData(leave);
    }

    /**
     * Set extra from validations from data
     */
    private setValidationsFromData(leave: Baja) {
        // fecha validations
        this.form
            .get('fecha')
            .setValidators([
                DateValidators.maxDate(moment()),
                DateValidators.minDate(moment(leave.avisoBaja)),
            ]);

        this.form.updateValueAndValidity();
    }

    /**
     * Fill data using an actuation
     */
    private fillData(actuation: Actuation) {
        this.title = actuation.tipoActuacion;

        this.form.patchValue(actuation);
        this.form.updateValueAndValidity();
    }

    /**
     * Get actation data
     */
    private async getActuationData(id: number): Promise<Actuation> {
        let actuation: Actuation = null;

        try {
            const res: RestResponse<Actuation> = await this.actuationService
                .getActuation(id)
                .toPromise();

            if (res.code === 0) {
                actuation = res.result;
            } else {
                console.error('Server Error: ' + res.message);
            }
        } catch (error) {
            console.error(error);
        }

        return actuation;
    }

    /**
     * Get leave data
     */
    private async getLeaveData(id: number) {
        let leave: Baja = null;

        try {
            const res: RestResponse<Baja> = await this.leaveService
                .GetLeaveById(id.toString())
                .toPromise();

            if (res.code === 0) {
                leave = res.result;
            } else {
                console.error('Server Error: ' + res.message);
            }
        } catch (error) {
            console.error(error);
        }

        return leave;
    }

    /**
     * Update request
     */
    private async updateSolicitud(
        id: number,
        solicitud: Actuation
    ): Promise<Actuation> {
        let updatedSolicitud: Actuation = null;

        try {
            const res = await this.actuationService
                .updateSolicitud(id, solicitud)
                .toPromise();

            if (res.code === 0) {
                updatedSolicitud = res.result;
            } else {
                console.error('Server Error: ' + res.message);
            }
        } catch (error) {
            console.error(error);
        }

        return updatedSolicitud;
    }

    /**
     * Convert form to solicitud
     */
    private convertFormToSolicitud() {
        const formValue = this.form.value;
        formValue.fecha = this.dateUtils.convertMomentForBackend(
            formValue.fecha
        );
        formValue.frespuestaSolicitud = this.dateUtils.convertMomentForBackend(
            formValue.frespuestaSolicitud
        );
        formValue.fprogramadaSolicitud = this.dateUtils.convertMomentForBackend(
            formValue.fprogramadaSolicitud
        );
        formValue.frealizacionSolicitud =
            this.dateUtils.convertMomentForBackend(
                formValue.frealizacionSolicitud
            );
        return formValue as Actuation;
    }

    //#endregion
}
